import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { ISession, IUser } from "./proto";

export type Role = "customer" | "ops_agent" | "ops_admin";

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api.me(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function isOps(me: IUser | null | undefined): boolean {
  return me?.role === "ops_agent" || me?.role === "ops_admin";
}

export function isAdmin(me: IUser | null | undefined): boolean {
  return me?.role === "ops_admin";
}

export function hasName(me: IUser | null | undefined): boolean {
  return !!me?.name?.trim();
}

export function profileComplete(me: IUser | null | undefined): boolean {
  return !!(me?.name && me?.locationArea && me?.town);
}

export function useCompleteSignIn() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return useCallback(
    async (session: ISession) => {
      qc.setQueryData(["me"], session.user);
      if (isOps(session.user)) await navigate({ to: "/ops" });
      else if (!hasName(session.user)) await navigate({ to: "/onboarding" });
      else await navigate({ to: "/" });
    },
    [navigate, qc],
  );
}
